/**
 * API configuration for the CrewAI RAG application
 * Sets up axios with base URL and interceptors for authentication
 * Uses environment variables for all configuration
 */
import axios from 'axios';
import authService from './auth';

// Get configuration from environment variables
const API_CONFIG = {
  baseURL: process.env.REACT_APP_USE_PROXY === 'true' ? '' : process.env.REACT_APP_API_URL,
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
  retryCount: parseInt(process.env.REACT_APP_REQUEST_RETRY_COUNT) || 3,
  retryDelay: parseInt(process.env.REACT_APP_REQUEST_RETRY_DELAY) || 1000,
  debugApiCalls: process.env.REACT_APP_DEBUG_API_CALLS === 'true'
};

// Create axios instance with environment-based config
const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: API_CONFIG.timeout,
});

// Add request interceptor for authentication and logging
api.interceptors.request.use(
  (config) => {
    // Add auth token to requests if user is logged in
    const user = authService.getCurrentUser();
    if (user && user.access_token) {
      config.headers['Authorization'] = `Bearer ${user.access_token}`;
    }

    // Log request details for debugging
    if (API_CONFIG.debugApiCalls) {
      console.debug('API request', { method: config.method });
    }
    return config;
  },
  (error) => {
    console.error('API request could not be sent');
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling and logging
api.interceptors.response.use(
  (response) => {
    // Log successful response for debugging
    if (API_CONFIG.debugApiCalls) {
      console.debug('API response', { status: response.status });
    }
    return response;
  },
  (error) => {
    // Log error response for debugging
    console.error('API request failed', { status: error.response?.status });

    // Handle 401 Unauthorized errors (token expired)
    if (error.response && error.response.status === 401) {
      authService.logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
